import { Mp3Encoder } from '@breezystack/lamejs';

/**
 * Convert a MediaRecorder clip to MP3 for WhatsApp delivery.
 *
 * Why not send the recording as-is: Chrome's `audio/mp4` recordings are
 * FRAGMENTED MP4s. Browsers play them, but Meta's media processor rejects
 * them ("Media upload error"), so a voice note recorded in Chrome never
 * reached the customer. MP3 (`audio/mpeg`) is on WhatsApp's supported list
 * and every browser can DECODE its own recording via WebAudio - so we
 * decode to PCM and re-encode to mono MP3 client-side (~1s for a typical
 * clip; lamejs is pure JS, no wasm/network).
 */
export async function voiceClipToMp3(blob: Blob): Promise<File> {
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(await blob.arrayBuffer());

    // Downmix to mono - voice notes don't need stereo and it halves the size.
    const channels = decoded.numberOfChannels;
    const length = decoded.length;
    const mono = new Float32Array(length);
    for (let c = 0; c < channels; c += 1) {
      const data = decoded.getChannelData(c);
      for (let i = 0; i < length; i += 1) mono[i] = (mono[i] ?? 0) + (data[i] ?? 0) / channels;
    }

    // Float [-1, 1] -> 16-bit PCM.
    const pcm = new Int16Array(length);
    for (let i = 0; i < length; i += 1) {
      const v = Math.max(-1, Math.min(1, mono[i] ?? 0));
      pcm[i] = v < 0 ? v * 0x8000 : v * 0x7fff;
    }

    const encoder = new Mp3Encoder(1, decoded.sampleRate, 96);
    const chunks: Uint8Array[] = [];
    const BLOCK = 1152; // one MPEG frame of samples
    for (let i = 0; i < pcm.length; i += BLOCK) {
      const out = encoder.encodeBuffer(pcm.subarray(i, i + BLOCK));
      if (out.length > 0) chunks.push(out);
    }
    const tail = encoder.flush();
    if (tail.length > 0) chunks.push(tail);

    return new File(chunks as BlobPart[], `voice-note-${Date.now()}.mp3`, {
      type: 'audio/mpeg',
    });
  } finally {
    void ctx.close();
  }
}
